import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag, CornerDownRight } from "lucide-react";

const empty = { name: "", slug: "", description: "", gst_rate: 18, display_order: 0, is_active: true, parent_id: null };

// Build the set of ids that would create a cycle if chosen as a parent: the
// category itself + every descendant. Used to disable invalid options.
function descendantIdSet(rootId, all) {
    if (!rootId) return new Set();
    const out = new Set([rootId]);
    const stack = [rootId];
    while (stack.length) {
        const pid = stack.pop();
        for (const c of all) {
            if (c.parent_id === pid && !out.has(c.id)) {
                out.add(c.id);
                stack.push(c.id);
            }
        }
    }
    return out;
}

function CategoryEditor({ open, onOpenChange, initial, onSaved, allCategories }) {
    const isEdit = Boolean(initial?.id);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm(initial ? { ...empty, ...initial } : empty);
    }, [open, initial]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked
                : (name === "gst_rate" || name === "display_order") ? Number(value)
                : value,
        }));
    };

    // When editing, the category itself and any of its descendants must not
    // be selectable as the new parent.
    const blocked = useMemo(
        () => descendantIdSet(initial?.id, allCategories),
        [initial?.id, allCategories],
    );

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                slug: form.slug?.trim() || undefined,
                parent_id: form.parent_id || null,
            };
            if (isEdit) {
                await api.patch(`/admin/categories/${initial.id}`, payload);
                toast.success("Category updated");
            } else {
                await api.post("/admin/categories", payload);
                toast.success("Category created");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="category-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        {isEdit ? "Edit category" : "New category"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Name</Label>
                            <Input name="name" value={form.name} onChange={onChange} required className="mt-1.5 rounded-xl" data-testid="category-form-name" />
                        </div>
                        <div>
                            <Label>Slug (auto if empty)</Label>
                            <Input name="slug" value={form.slug || ""} onChange={onChange} placeholder="auto-generated" className="mt-1.5 rounded-xl" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Parent category</Label>
                            <Select
                                value={form.parent_id || "__none__"}
                                onValueChange={(v) => setForm((f) => ({ ...f, parent_id: v === "__none__" ? null : v }))}
                            >
                                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="category-form-parent">
                                    <SelectValue placeholder="Top-level category" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— Top-level (no parent) —</SelectItem>
                                    {allCategories.map((c) => (
                                        <SelectItem
                                            key={c.id}
                                            value={c.id}
                                            disabled={blocked.has(c.id)}
                                        >
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-ink-700 mt-1">Sub-categories inherit nothing — set GST and visibility per category.</p>
                        </div>
                        <div>
                            <Label>GST rate (%)</Label>
                            <Input type="number" name="gst_rate" min={0} max={100} step={0.5} value={form.gst_rate} onChange={onChange} className="mt-1.5 rounded-xl" data-testid="category-form-gst" />
                        </div>
                        <div>
                            <Label>Display order</Label>
                            <Input type="number" name="display_order" value={form.display_order} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea name="description" value={form.description || ""} onChange={onChange} rows={2} className="mt-1.5 rounded-xl" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} />
                        Active (visible on storefront)
                    </label>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="category-form-save">
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Create category"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function CategoryRow({ c, depth = 0, onEdit, onDelete, onAddChild }) {
    return (
        <div
            className="rounded-2xl bg-white border border-gray-100 p-4 flex justify-between items-start"
            style={{ marginLeft: depth * 24 }}
            data-testid={`category-row-${c.id}`}
        >
            <div className="flex items-start gap-3 min-w-0">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${depth === 0 ? "bg-brand-50 text-brand-600" : "bg-paper-alt text-ink-700"}`}>
                    {depth === 0 ? <Tag className="h-4 w-4" /> : <CornerDownRight className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-ink-700 truncate">{c.slug} · GST {c.gst_rate}% {c.is_active ? "" : "· hidden"}</div>
                </div>
            </div>
            <div className="flex gap-1.5">
                <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => onAddChild(c)} title="Add sub-category" data-testid={`category-add-child-${c.id}`}>
                    <Plus className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => onEdit(c)} data-testid={`category-edit-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => onDelete(c.id)} data-testid={`category-delete-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    );
}

export default function CategoriesTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const reload = () => api.get("/admin/categories").then(({ data }) => setItems(data));
    useEffect(() => { reload(); }, []);

    const remove = async (id) => {
        if (!confirm("Delete this category? Products in it will be uncategorised and its sub-categories will become top-level.")) return;
        try {
            await api.delete(`/admin/categories/${id}`);
            toast.success("Category deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    // Build a tree view in render order: top-level first, each followed by its descendants in BFS.
    const ordered = useMemo(() => {
        const byParent = new Map();
        for (const c of items) {
            const key = c.parent_id || "__root__";
            if (!byParent.has(key)) byParent.set(key, []);
            byParent.get(key).push(c);
        }
        for (const arr of byParent.values()) {
            arr.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || a.name.localeCompare(b.name));
        }
        const out = [];
        const walk = (parentKey, depth) => {
            const children = byParent.get(parentKey) || [];
            for (const c of children) {
                out.push({ c, depth });
                walk(c.id, depth + 1);
            }
        };
        walk("__root__", 0);
        // Surface any orphan (parent_id references a missing category) at top-level.
        const seenIds = new Set(out.map((x) => x.c.id));
        for (const c of items) {
            if (!seenIds.has(c.id)) out.push({ c, depth: 0 });
        }
        return out;
    }, [items]);

    return (
        <div className="space-y-4" data-testid="categories-tab">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="font-heading text-2xl">Categories</h3>
                    <p className="text-sm text-ink-700">Group products, nest sub-categories under a parent, and set per-category GST rates.</p>
                </div>
                <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="category-new-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> New category
                </Button>
            </div>
            <div className="space-y-2">
                {ordered.map(({ c, depth }) => (
                    <CategoryRow
                        key={c.id}
                        c={c}
                        depth={depth}
                        onEdit={(cat) => { setEditing(cat); setOpen(true); }}
                        onDelete={remove}
                        onAddChild={(parent) => { setEditing({ ...empty, parent_id: parent.id }); setOpen(true); }}
                    />
                ))}
                {items.length === 0 && (
                    <div className="text-center py-10 text-ink-700">No categories yet — click "New category" to add one.</div>
                )}
            </div>
            <CategoryEditor
                open={open}
                onOpenChange={setOpen}
                initial={editing}
                onSaved={reload}
                allCategories={items}
            />
        </div>
    );
}
